<script setup lang="ts">
import { ref, onMounted, computed, toRef } from 'vue';
import { useField } from 'vee-validate';
import { useFieldLazy } from '@/composables/useFieldLazy';

const props = withDefaults(defineProps<{
	modelValue?: string;
	standalone?: boolean;
	rules?: Parameters<typeof useField>[1];
	placeholder?: string;
	autofocus?: boolean;
	name: string;
}>(), {
	modelValue: undefined,
	standalone: false,
	rules: undefined,
	placeholder: '',
	autofocus: false,
});

defineEmits<{
	(e: 'update:modelValue', value: string): void;
}>();

const inputRef = ref<HTMLInputElement>();
const focusInput = () => {
	inputRef.value?.focus();
	inputRef.value?.select();
};

const {
	value: inputValue,
	validationListeners,
	errorMessage,
	meta,
} = useFieldLazy(
	toRef(props, 'name'),
	props.rules,
	{
		syncVModel: true,
		standalone: props.standalone,
	},
);

const isInvalid = computed(() => {
	if (!meta) {
		return false;
	}

	return (meta.validated || meta.touched) && !meta.valid;
});

const autoFocusInput = () => {
	if (!props.autofocus || props.modelValue !== '') {
		return;
	}

	const isTouchDevice = 'ontouchstart' in document.documentElement;

	if (window.innerWidth > 942 || !isTouchDevice) {
		focusInput();
	}
};

onMounted(autoFocusInput);
</script>

<script lang="ts">
export default {
	inheritAttrs: false,
};
</script>

<template>
	<div>
		<div class="relative rounded-lg">
			<input
				ref="inputRef"
				v-model="inputValue"
				class="focus:ring-0 focus:outline-none appearance-none text-white bg-primary-300 rounded-md w-full px-2 text-2xl border border-primary-900 py-2 focus:border-white"
				:class="{
					'!border-secondary':
						isInvalid && (meta?.validated || meta?.touched),
				}"
				type="text"
				:name="name"
				:aria-label="name"
				:placeholder="placeholder"
				v-bind="$attrs"
				v-on="validationListeners"
			>
		</div>
		<div
			v-if="errorMessage"
			class="text-xs text-secondary mt-1"
		>
			{{ errorMessage }}
		</div>
	</div>
</template>
